import { z } from 'zod';
import { isAxiosError } from 'axios';

const objectIdSchema = z.string().regex(/^[0-9a-f]{24}$/);

export const themeImportItemSchema = z.object({
  id: objectIdSchema,
  filenames: z.array(z.string().min(1).max(500)).min(1).max(20),
  title: z.string().max(500),
  sectorName: z.string().max(255),
  questionsCount: z.number().int().nonnegative(),
  createdAt: z.string().datetime(),
  uploadedBy: z.object({
    id: z.number().int().positive(),
    username: z.string().min(1).max(255),
  }).strict().nullable(),
  status: z.enum(['active', 'inactive']),
}).strict();

export const themeImportHistoryResponseSchema = z.object({
  error: z.literal(false),
  status: z.literal(200),
  message: z.literal('THEME_IMPORTS_READ'),
  data: z.array(themeImportItemSchema).max(50),
  meta: z.object({
    request_id: z.string().uuid(),
    served_at: z.string().datetime(),
    pagination: z.object({
      limit: z.number().int().min(1).max(50),
      next_cursor: z.string().min(1).max(64).nullable(),
      has_more: z.boolean(),
    }).strict(),
  }).strict(),
}).strict();

export const themeImportQuestionSchema = z.object({
  id: objectIdSchema,
  questionId: z.string().min(1).max(64),
  themeId: z.string().max(64).nullable(),
  sectorId: z.string().max(64),
  questionText: z.string().max(2000),
  language: z.string().max(64).nullable(),
  order: z.number().int().positive().nullable(),
  domain: z.string().max(255).nullable(),
  source: z.string().max(255).nullable(),
  createdAt: z.string().datetime().nullable(),
}).strict();

export const themeImportQuestionsResponseSchema = z.object({
  error: z.literal(false),
  status: z.literal(200),
  message: z.literal('THEME_QUESTIONS_READ'),
  data: z.array(themeImportQuestionSchema).max(300),
  meta: z.object({
    request_id: z.string().uuid(),
    served_at: z.string().datetime(),
    pagination: z.object({
      total: z.number().int().nonnegative(),
      page: z.number().int().min(1),
      limit: z.number().int().min(1).max(300),
      pages: z.number().int().nonnegative(),
      has_more: z.boolean(),
    }).strict(),
  }).strict(),
}).strict();

export type ThemeImportItem = z.infer<typeof themeImportItemSchema>;
export type ThemeImportHistory = z.infer<typeof themeImportHistoryResponseSchema>;
export type ThemeImportQuestion = z.infer<typeof themeImportQuestionSchema>;
export type ThemeImportQuestions = z.infer<typeof themeImportQuestionsResponseSchema>;

const errorCodeSchema = z.enum([
  'VALIDATION_FAILED', 'UNAUTHORIZED', 'FORBIDDEN', 'NOT_FOUND',
  'IMPORT_INACTIVE', 'QUESTION_CONFLICT', 'INTERNAL_SERVER_ERROR',
  'REAUTHENTICATION_FAILED',
]);

const errorEnvelopeSchema = z.object({
  error: z.literal(true),
  status: z.number().int().min(400).max(599),
  message: errorCodeSchema,
  data: z.null(),
  meta: z.object({
    request_id: z.string().uuid(),
    served_at: z.string().datetime(),
    retryable: z.boolean().optional(),
    violations: z.array(z.string()).max(100).optional(),
  }).strict(),
}).strict();

export function themeImportErrorMessage(error: unknown, messages: Record<string, string>): string {
  if (!isAxiosError(error)) return messages.DEFAULT;
  const parsed = errorEnvelopeSchema.safeParse(error.response?.data);
  if (!parsed.success || parsed.data.status !== error.response?.status) return messages.DEFAULT;
  return messages[parsed.data.message];
}

export const themeImportHistoryErrorMessage = themeImportErrorMessage;
